import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import { fetchSessions } from '@/features/sessions/api'
import type { SessionRecord } from '@/features/sessions/types'

/**
 * Resolve the computer-use session(s) that originated from a given call.
 * Sessions record their originating call in metadata->>'source_id'
 * (with source_type='call'); a call usually maps to one session but can
 * map to a few when multiple workflows run off the same call.
 */
export function useCallSessions(callId: string | null) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  const query = useQuery({
    queryKey: ['call-sessions', callId, organization?.id],
    queryFn: async (): Promise<SessionRecord[]> => {
      const token = await getToken()
      const response = await fetchSessions(token, null, {
        source_id: callId ?? undefined,
        // A call maps to at most a handful of sessions; cap defensively.
        limit: 10,
      })
      return response.data
    },
    enabled: !!callId,
    staleTime: 5 * 60 * 1000,
  })

  return {
    sessions: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  }
}
